<template>
  <BaseDialog
    ref="dialog"
    contentClass="platforms-detail"
    sidePanel
    :persistent="false"
    :on-close="closeDialog"
    @closeModal="closeDialog"
  >
    <template slot="title">
      <div class="platforms-detail__header">
        <span class="platforms-detail__header-title">{{ $t('search-filters.platform-filter.details-title') }}</span>
        <BaseInfoBanner
          class="platforms-detail__banner"
          :tone="selectedMaintenanceCount ? 'warning' : 'info'"
          preset="soft"
          :title="
            $tc('search-filters.platform-filter.selected-count', selectedCount, { count: selectedCount }) +
            ' ' +
            maintenanceDescription
          "
        >
          <template #icon>
            <BaseIcon name="check" />
          </template>
        </BaseInfoBanner>
      </div>
    </template>
    <template slot="content">
      <div class="platforms-detail">
        <template v-for="section in sections">
          <h3 v-if="section.title" :key="section.key + '-title'" class="platforms-detail__section-title">
            {{ section.title }}
          </h3>
          <PlatformCard
            v-for="platform in section.platforms"
            :key="platform.id"
            :platform="platform"
            selectable
            :selected="isSelected(platform.id)"
            @select="onPlatformCheck(platform.id, $event)"
          />
        </template>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import BaseInfoBanner from '@/components/base/BaseInfoBanner';
import PlatformCard from '@/components/PlatformCard';
import { PLATFORM_DISPLAY_ORDER, sortPlatformsByDisplayOrder } from '@/util/constants/platform-display-order';

/**
 * Wide right-side drawer: accessible platforms as a selectable grid of PlatformCards. Platforms
 * tagged `old` are listed in a trailing "obsolete platforms" section. The header banner reports
 * how many platforms are selected and how many of those are in maintenance.
 */
export default {
  name: 'PlatformsDetailDialog',
  components: { BaseDialog, BaseIcon, BaseInfoBanner, PlatformCard },
  props: {
    platforms: { type: Array, default: () => [] },
    // Selected platform ids — same contract as PlatformChip / FilterMultiChip.
    value: { type: Array, default: () => [] },
    // Optional platform-id order override. Defaults to PLATFORM_DISPLAY_ORDER.
    order: { type: Array, default: () => PLATFORM_DISPLAY_ORDER },
  },
  computed: {
    accessiblePlatforms() {
      return sortPlatformsByDisplayOrder(this.platforms, this.order).filter((platform) => platform.hasAccess);
    },
    currentPlatforms() {
      return this.accessiblePlatforms.filter((platform) => !this.isObsolete(platform));
    },
    obsoletePlatforms() {
      return this.accessiblePlatforms.filter((platform) => this.isObsolete(platform));
    },
    sections() {
      const sections = [{ key: 'current', title: null, platforms: this.currentPlatforms }];
      if (this.obsoletePlatforms.length) {
        sections.push({
          key: 'obsolete',
          title: this.$t('search-filters.platform-filter.obsolete-title'),
          platforms: this.obsoletePlatforms,
        });
      }
      return sections;
    },
    selectedCount() {
      return (this.value || []).length;
    },
    selectedPlatforms() {
      const ids = new Set(this.value || []);
      return this.accessiblePlatforms.filter((platform) => ids.has(platform.id));
    },
    selectedMaintenanceCount() {
      return this.selectedPlatforms.filter((platform) => (platform.tags || []).includes('maintenance')).length;
    },
    maintenanceDescription() {
      if (!this.selectedMaintenanceCount) return '';
      return this.$tc('search-filters.platform-filter.selected-maintenance', this.selectedMaintenanceCount, {
        count: this.selectedMaintenanceCount,
      });
    },
  },
  methods: {
    isObsolete(platform) {
      return (platform.tags || []).includes('old');
    },
    isSelected(platformId) {
      return (this.value || []).includes(platformId);
    },
    onPlatformCheck(platformId, checked) {
      const current = this.value || [];
      const next = checked
        ? current.includes(platformId)
          ? current
          : [...current, platformId]
        : current.filter((id) => id !== platformId);
      this.$emit('change', next);
    },
    openDialog() {
      this.$refs.dialog.openModal();
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
      this.$emit('close');
    },
  },
  mounted() {
    this.$nextTick(() => this.openDialog());
  },
};
</script>

<style lang="scss">
.base-dialog__platforms-detail {
  width: min(1200px, 100vw);
  max-width: 100%;

  .base-dialog__header {
    height: auto;
    min-height: 75px;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid $color-border;

    .header__title {
      margin: 12px 1rem 12px 1.5rem;
    }

    .header__close-icon {
      margin-top: 4px;
    }
  }

  .base-dialog__header + .base-dialog__content {
    background-color: $color-neutral-pale;
    padding: 20px 24px;
  }
}

.platforms-detail {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  // Never stretch row peers to an expanded card — collapsed cards keep a fixed min-height.
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }

  &__header {
    display: flex;
    flex-direction: row;
    gap: 10px;
    min-width: 0;
    align-items: center;
  }

  &__header-title {
    display: block;
  }

  &__banner {
    max-width: 420px;
  }

  &__section-title {
    grid-column: 1 / -1;
    margin: 18px 0 0;
    font-family: $font-family-raleway;
    font-size: 14px;
    font-weight: 800;
    color: $color-slate-500;
    letter-spacing: 0.01em;
  }
}
</style>
